<script lang="ts">
	// The design system's CartLine (design/components/surfaces/CartLine.jsx):
	// one line of the order.
	//
	// Two targets side by side, never nested: the line opens its editor, and
	// Remove is its own button in its own cell at the inline end, behind a
	// hairline. Remove is the quiet close glyph, because the undo is the
	// safety net; a glyph draws the same weight in both fonts, where the ×
	// it replaced followed whichever face the language loaded.
	// Quantity is its own start column so names align down the list; the total
	// stays at the end; modifiers and the note wrap under name and total.
	//
	// The line presses with the design's invert; remove has its own bare press,
	// so what inverts is exactly what will act.
	//
	// A sent line (#348) is on the order. It has no Remove: the end cell holds
	// the check glyph over the word Sent instead. The line itself stays a
	// target when onOpen is passed: on an open order it opens Cancel item
	// (T19, #379), the only place a sent line changes; without onOpen it is a
	// plain row. The same grid and the same end cell keep the totals in one
	// column down a cart that mixes both. Its ground steps back to the card
	// header's, with the default hairline, which still shows under the dark
	// theme.
	//
	// The app adds test ids. The modifiers and the note share one, as a
	// display: contents wrapper, so the line's grid still lays them out.

	import Icon from './Icon.svelte';
	import { pressable } from './pressable';

	interface Props {
		lineKey: string;
		qty: number;
		name: string;
		modifiers: string[];
		note: string;
		/** Already formatted, Latin digits. */
		total: string;
		removeLabel: string;
		/** The line is on the order: no Remove, marked with sentLabel, and a target only with onOpen. */
		sent?: boolean;
		sentLabel?: string;
		onOpen?: () => void;
		onRemove?: () => void;
	}

	let {
		lineKey,
		qty,
		name,
		modifiers,
		note,
		total,
		removeLabel,
		sent = false,
		sentLabel = '',
		onOpen,
		onRemove,
	}: Props = $props();

	const mods = $derived(modifiers.filter(Boolean).join(' · '));
</script>

{#snippet cells()}
	<span class="qty"><bdi dir="ltr">{qty}×</bdi></span>
	<span class="name">{name}</span>
	<span class="total" data-testid="line-total-{lineKey}"><bdi dir="ltr">{total}</bdi></span>
	{#if mods || note}
		<span class="sub" data-testid="line-sub-{lineKey}">
			{#if mods}
				<span class="mods">{mods}</span>
			{/if}
			{#if note}
				<span class="note">{note}</span>
			{/if}
		</span>
	{/if}
{/snippet}

{#if sent}
	<div class="line sent" role="listitem" data-testid="cart-row-{lineKey}">
		{#if onOpen}
			<button
				type="button"
				class="open"
				data-testid="line-sent-{lineKey}"
				data-press="invert"
				onclick={onOpen}
				{@attach pressable}
			>
				{@render cells()}
			</button>
		{:else}
			<div class="open" data-testid="line-sent-{lineKey}">
				{@render cells()}
			</div>
		{/if}
		<span class="end mark">
			<Icon name="check" size={16} />
			<span class="mark-label">{sentLabel}</span>
		</span>
	</div>
{:else}
	<div class="line" role="listitem" data-testid="cart-row-{lineKey}">
		<button
			type="button"
			class="open"
			data-testid="line-edit-{lineKey}"
			data-press="invert"
			onclick={onOpen}
			{@attach pressable}
		>
			{@render cells()}
		</button>
		<button
			type="button"
			class="end remove"
			data-testid="line-remove-{lineKey}"
			aria-label="{removeLabel} {name}"
			data-press="bare"
			onclick={onRemove}
			{@attach pressable}
		>
			<Icon name="close" />
		</button>
	</div>
{/if}

<style>
	.line {
		display: flex;
		align-items: stretch;
		background: var(--surface-card);
		border-block-end: var(--border-width) solid var(--border-inset);
	}

	.open {
		flex: 1;
		min-inline-size: 0;
		display: grid;
		grid-template-columns: auto minmax(0, 1fr) auto;
		column-gap: 10px;
		row-gap: 2px;
		align-items: baseline;
		align-content: center;
		min-block-size: var(--ctl-min);
		padding: 12px 14px;
		text-align: start;
		background: var(--surface-card);
		color: var(--text-strong);
		border: none;
		border-radius: 0;
		font-family: var(--font-sans);
		cursor: pointer;
	}

	/* In dark, --surface-card-header equals --border-inset, so a sent line's
	   hairlines take the default rule or they would vanish into its ground. */
	.sent {
		background: var(--surface-card-header);
		border-block-end-color: var(--border-default);
	}

	.sent .open {
		background: var(--surface-card-header);
		cursor: default;
	}

	.sent button.open {
		cursor: pointer;
	}

	.qty,
	.total {
		font-family: var(--font-mono);
		font-size: 15px;
		font-weight: 700;
		color: var(--text-strong);
	}

	.total {
		white-space: nowrap;
	}

	.name {
		min-inline-size: 0;
		font-size: 15px;
		font-weight: 600;
		line-height: 1.45;
		color: var(--text-strong);
		overflow-wrap: anywhere;
	}

	.sub {
		display: contents;
	}

	.mods,
	.note {
		grid-column: 2 / 4;
		min-inline-size: 0;
		font-size: var(--text-2xs);
		line-height: 1.55;
	}

	.mods {
		color: var(--text-body);
	}

	.note {
		color: var(--text-muted);
	}

	.end {
		flex: none;
		inline-size: var(--tap-min);
		padding: 0;
		background: transparent;
		border: none;
		border-inline-start: var(--border-width) solid var(--border-inset);
	}

	.remove {
		display: grid;
		place-items: center;
		color: var(--text-muted);
		cursor: pointer;
	}

	.mark {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 2px;
		color: var(--text-body);
		border-inline-start-color: var(--border-default);
	}

	.mark-label {
		font-size: var(--text-micro);
		font-weight: 600;
		line-height: 1.2;
		white-space: nowrap;
	}
</style>
